import { Col, Row } from 'antd';
import {
  ApplicantPreviewBlock,
  BillingPreviewBlock,
  ComplementingFactsPreviewBlock,
  DateAndTimePreviewBlock,
  LeaseholdInfoPreviewBlock,
  LeaseholdTypePreviewBlock,
  MotivationPreviewBlock,
  PaddingBlock,
  PreviewBlock
} from 'lib/Antd/Preview';
import { types } from 'mobx-state-tree';
import styled from 'styled-components';
import { CaseStatus, Panel, Step } from 'utils/enumerations';
import { leaseholdPanelOrderList, leaseholdStepPanels, lhPanelWithKeys, panelHasAnyValue } from 'utils/panelUtils';
import { ApplicationBaseModel } from './applicationBaseModel';

const CompletePage = styled.div`
  height: 22.3cm;
`;
//22.3 actual paper height excluding the paper margin and the header margin

const LeaseholdSpecificModel = types
  .model('LeaseholdSpecificModel', {
    purpose: types.maybe(types.string),
    type: types.maybe(types.string),
    originRegistrationDate: types.maybe(types.string),
    decisionDate: types.maybe(types.string),
    leaseholdType: types.maybe(types.integer),
    createdInternally: types.maybe(types.boolean),
    initialStatus: types.maybe(types.integer),
    locationDescription: types.maybe(types.string),
    location: types.maybe(types.string),
    policeCaseNumber: types.maybe(types.string),
    internallyCreatedBy: types.maybe(types.string),
    statement: types.maybe(types.string),
    applicationAdditionalInfo: types.maybe(types.string)
  })
  .views(self => ({
    get name() {
      return self.purpose;
    }
  }));

export const LeaseholdModel = types
  .compose(ApplicationBaseModel, LeaseholdSpecificModel)
  .named('LeaseholdModel')
  .views(self => ({
    getPreview(t) {
      return (
        <>
          {self.status >= CaseStatus.Processed && (
            <CompletePage>
              <LeaseholdInfoPreviewBlock
                purpose={self.purpose}
                policeCaseNumber={self.policeCaseNumber}
                caseStatus={self.status}
                location={self.location}
                currentPanel={self.currentPanel}
              />
              <MotivationPreviewBlock
                answer={self.statement ? self.statement : self.approvalStatement}
                handlingOfficerName={self.handlingOfficerName}
              />
            </CompletePage>
          )}
          <LeaseholdInfoPreviewBlock
            purpose={self.purpose}
            policeCaseNumber={self.policeCaseNumber}
            caseStatus={self.status}
            location={self.location}
            currentPanel={self.currentPanel}
          />
          {self.isPanelTouchedOrHasValue(Panel.LeaseholdTypes) && (
            <LeaseholdTypePreviewBlock
              type={self.type}
              leaseholdType={self.leaseholdType}
              currentPanel={self.currentPanel}
            />
          )}
          {self.isPanelTouchedOrHasValue(Panel.Questioneer) && (
            <ApplicantPreviewBlock
              applicant={self.applicant}
              billingInfo={self.billingInformation}
              currentPanel={self.currentPanel}
            />
          )}
          {self.isPanelTouchedOrHasValue(Panel.InvoiceAddress) && (
            <BillingPreviewBlock
              billingInfo={self.billingInformation}
              applicant={self.applicant}
              currentPanel={self.currentPanel}
            />
          )}
          {self.isPanelTouchedOrHasValue(Panel.DateAndTimePeriod) && (
            <DateAndTimePreviewBlock
              dateTimeInfo={self.dateTimeInformation}
              prolongedTo={self.prolongedTo}
              prolongedStatus={self.prolongedStatus}
              currentPanel={self.currentPanel}
            />
          )}
          {self.childCases.length > 0 ||
          self.parentCases.length ||
          self.isPanelTouchedOrHasValue(Panel.AdditionalInformation) ? (
            <ComplementingFactsPreviewBlock
              additionalInformation={self.applicationAdditionalInfo}
              attachments={self.attachments}
              childCases={self.childCases}
              parentCases={self.parentCases}
              currentPanel={self.currentPanel}
            />
          ) : null}
          {self.geometrySnapshot && (
            <CompletePage isMapPage>
              <PreviewBlock step={Step.Map} panel={Panel.Extent}>
                <Col span={24} offset={0}>
                  <PaddingBlock />
                  <Row justify="start">
                    <img
                      style={{ width: '100%' }}
                      className="preview-map"
                      src={self.geometrySnapshot}
                      alt={t('preview:extent')}
                    />
                  </Row>
                </Col>
              </PreviewBlock>
            </CompletePage>
          )}
        </>
      );
    },
    getPanelByKey: itemKey => {
      return lhPanelWithKeys.find(panel => panel.keys.find(panelKey => panelKey === itemKey))?.panelName;
    }
  }))
  .actions(self => ({
    isPanelTouchedOrHasValue: panel => {
      if (self.status >= CaseStatus.Registered) {
        self.setTouchedPanelIndex(panel);
        return true;
      }
      const currentPanelIndex = leaseholdPanelOrderList.findIndex(touchedPanel => touchedPanel === panel);
      if (currentPanelIndex <= self.touchedPanelIndex) {
        return true;
      } else if (panelHasAnyValue(self.toJSON(), panel, lhPanelWithKeys)) {
        self.setTouchedPanelIndex(panel);
        return true;
      }
      return false;
    },
    setTouchedPanelIndex: panel => {
      const currentPanelIndex = leaseholdPanelOrderList.findIndex(touchedPanel => touchedPanel === panel);
      if (self.touchedPanelIndex < currentPanelIndex) {
        self.touchedPanelIndex = currentPanelIndex;
        const step = leaseholdStepPanels.find(steps => steps.panels.find(panelName => panel === panelName))?.step;
        self.setTouchedStep(step);
      }
    }
  }));
